import { useMemo, useState, type Dispatch } from "react";
import { useTranslation } from "react-i18next";
import { useCitizenshipCountries } from "@src/api/useCitizenshipCountries";
import chevronDown from "@src/assets/icons/chevronDown.svg";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from "@src/components/ui/dialog";
import { Input } from "@src/components/ui/input";
import { cn } from "@src/lib/utils";
import { useBirthCountries } from "@src/api/useBirthCountries";

interface TProp {
  countryId: number;
  setCountryId: Dispatch<number>;
  withCountryDesc?: boolean;
  forBirth?: boolean;
  disabled?: boolean;
  label?: string;
  withoutCountryId?: number;
}

function CountrySelect({
  countryId,
  setCountryId,
  withCountryDesc,
  forBirth,
  disabled,
  label,
  withoutCountryId,
}: TProp) {
  const { t } = useTranslation();
  const countries = (forBirth ? useBirthCountries : useCitizenshipCountries)();
  const [keyword, setKeyword] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const country = useMemo(
    () => countries.data?.countries.find((c) => c.countryID === countryId),
    [countries.data?.countries, countryId]
  );

  const openDialog = () => {
    if (!disabled) setIsDialogOpen(true);
  };
  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const searchedCountries = useMemo(() => {
    const listOfCountries = countries.data?.countries.filter(
      (c) =>
        c.countryName?.toLowerCase().includes(keyword.toLowerCase()) &&
        c.countryID !== withoutCountryId
    );
    if (listOfCountries?.length) {
      return listOfCountries;
    }
    return [];
  }, [countries.data?.countries, keyword, withoutCountryId]);

  return (
    <Dialog onOpenChange={closeDialog} open={isDialogOpen}>
      <div>
        { }
        <div
          className={cn(
            "flex h-14 cursor-pointer items-center justify-between rounded-lg border border-solid border-gray-200 px-4 py-2",
            disabled && "cursor-not-allowed bg-gray-100"
          )}
          onClick={openDialog}
        >
          <div>
            {country ? (
              <p className="text-xs text-gray-700">
                {label || t("citizenship.inputs.passportCountry")}
              </p>
            ) : null}
            <p
              className={cn(
                country?.countryName ? "text-textBlack" : "text-gray-500"
              )}
            >
              {country?.countryName ||
                label ||
                t("citizenship.inputs.passportCountry")}
            </p>
          </div>
          <img
            alt="chevronDown"
            className="h-6 w-6 rounded-full"
            src={chevronDown}
          />
        </div>
        {withCountryDesc ? (
          <p className="mt-1 text-sm">{t("citizenship.inputs.countryDesc")}</p>
        ) : null}
      </div>
      <DialogContent
        className={cn("w-[375px] rounded-xl bg-white p-0 md:w-full", searchedCountries.length > 0 ? "h-auto" : "h-[250px]")}
        withoutX
      >
        <DialogHeader>
          <Input
            className="border-b border-solid border-gray-200 p-4"
            onChange={(e) => {
              setKeyword(e.target.value);
            }}
            placeholder={t("search")}
            value={keyword}
          />
          {countries.isLoading ? (
            <DialogDescription className="h-[364px] overflow-auto animate-pulse">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => {
                return (
                  <div
                    className="h-13 w-[calc(100%-16px)] rounded bg-gray-200 m-2"
                    key={i}
                  />
                );
              })}
            </DialogDescription>
          ) : (
            <DialogDescription className="h-[364px] overflow-auto">
              {searchedCountries.length ? (
                searchedCountries.map((c) => {
                  return (
                     
                    <div
                      className={cn(
                        "flex h-[52px] cursor-pointer items-center gap-2 border-b border-solid border-gray-200 px-4 py-2 text-left",
                        c.countryID === country?.countryID &&
                          "border-r-whitebitYellow border-r-4 border-solid"
                      )}
                      key={c.countryID}
                      onClick={() => {
                        closeDialog();
                        setCountryId(c.countryID);
                      }}
                    >
                      <img
                        alt="logo"
                        className="h-5 w-5 rounded-full object-contain"
                        src={c.flagURL}
                      />
                      <p className="text-textGray">{c.countryName}</p>
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center gap-3 p-4 text-center">
                  <div className="my-6 flex max-w-[400px] flex-col gap-2">
                    <p className="text-xl font-bold text-baseBlack">
                      {t("not_found_title")}
                    </p>
                    <p className="mt-2 leading-6 text-textBlack">
                      {t("not_found_desc")}
                    </p>
                  </div>
                </div>
              )}
            </DialogDescription>
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

export default CountrySelect;
